import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuthStore } from "@/modules/auth/store";
import { useMe, meQueryKey } from "@/api/hooks/auth";
import { useUpdateClientCommunicationEmail } from "@/api/hooks/clientportal";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Mandatory post-login email dialog for client users. Fires whenever the server
 * reports `needsEmailConfirmation` (client has never confirmed their
 * communication email). Blocking — no dismiss — until the client either sets a
 * new address or confirms the existing one. Renders nothing for internal roles.
 */
export function ClientEmailGate() {
  const role = useAuthStore((s) => s.user?.role);
  const isClient = role === "client";
  const me = useMe(isClient);
  const qc = useQueryClient();
  const update = useUpdateClientCommunicationEmail();

  const existing = me.data?.user.communicationEmail ?? "";
  const needs = isClient && me.data?.needsEmailConfirmation === true;

  const [value, setValue] = useState("");
  const [touched, setTouched] = useState(false);

  // Prefill with the existing address when the dialog opens so a confirm is
  // one click.
  useEffect(() => {
    if (needs) setValue(existing);
  }, [needs, existing]);

  if (!needs) return null;

  const trimmed = value.trim();
  const valid = EMAIL_RE.test(trimmed);
  const hasExisting = existing.trim().length > 0;

  const submit = async () => {
    setTouched(true);
    if (!valid) return;
    try {
      await update.mutateAsync(trimmed);
      await qc.invalidateQueries({ queryKey: meQueryKey });
      toast.success("Email confirmed");
    } catch {
      toast.error("Could not save your email. Please try again.");
    }
  };

  return (
    <Dialog open disableEscapeKeyDown maxWidth="xs" fullWidth onClose={() => {}}>
      <DialogTitle>{hasExisting ? "Confirm your email" : "Add your email"}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2, color: "var(--ink-2)" }}>
          {hasExisting
            ? "We use this address to send you document acknowledgements and updates. Confirm it is correct, or update it below."
            : "Please add an email address. We use it to send you document acknowledgements and updates."}
        </Typography>
        <TextField
          autoFocus
          fullWidth
          size="small"
          type="email"
          label="Communication email"
          placeholder="you@company.com"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => setTouched(true)}
          error={touched && !valid}
          helperText={touched && !valid ? "Enter a valid email address." : " "}
        />
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={submit}
          disabled={update.isPending || !valid}
        >
          {update.isPending ? "Saving..." : hasExisting ? "Confirm" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
